/**
 * v4.10.0 — Lightweight setup-guide state hook.
 *
 * Two responsibilities:
 *   1. Per-user, per-guide dismissal via localStorage (instant, no backend round-trip).
 *   2. One-shot fetch of the org's optional explainer URL — shared cache so opening
 *      multiple guidance cards on the same page does not refetch.
 *
 * Keys live under `yot.setup_guide.dismissed.<guideKey>` so they survive refresh
 * but stay scoped per-browser. Reopening via <HelpGlyph/> clears the flag.
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";

const LS_PREFIX = "yot.setup_guide.dismissed.";
let _explainerCache = null;
let _explainerPromise = null;

const fetchExplainer = async () => {
    if (_explainerCache !== null) return _explainerCache;
    if (_explainerPromise) return _explainerPromise;
    _explainerPromise = (async () => {
        try {
            const res = await api.get("/setup-guide/config");
            _explainerCache = res?.data?.onboarding_explainer_url || "";
        } catch {
            _explainerCache = "";
        }
        return _explainerCache;
    })();
    return _explainerPromise;
};

export const resetSetupGuide = (guideKey) => {
    try { localStorage.removeItem(LS_PREFIX + guideKey); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent("yot:setup-guide-reset", { detail: { guideKey } }));
};

export const refreshExplainerCache = () => {
    _explainerCache = null;
    _explainerPromise = null;
};

export const useSetupGuide = (guideKey) => {
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(LS_PREFIX + guideKey) === "1"; } catch { return false; }
    });
    const [explainerUrl, setExplainerUrl] = useState(_explainerCache || "");

    useEffect(() => {
        let cancelled = false;
        fetchExplainer().then((url) => { if (!cancelled) setExplainerUrl(url); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onReset = (e) => {
            if (e?.detail?.guideKey === guideKey) setDismissed(false);
        };
        window.addEventListener("yot:setup-guide-reset", onReset);
        return () => window.removeEventListener("yot:setup-guide-reset", onReset);
    }, [guideKey]);

    const dismiss = useCallback(() => {
        try { localStorage.setItem(LS_PREFIX + guideKey, "1"); } catch { /* noop */ }
        setDismissed(true);
    }, [guideKey]);

    return { dismissed, dismiss, explainerUrl };
};
